import { useEffect, useRef, useState } from 'react'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import Popup from '../../../../../../../../../../../../components/ui/account/popup'
import ButtonBlue from '../../../../../../../../../../../../components/ui/button/_color/_blue'
import ButtonWhite from '../../../../../../../../../../../../components/ui/button/_color/_white'
import { cancelTransaction } from '../../../../../../../../../../../../store/fetchers/users'
import { floorBigNumber } from '../../../../../../../../../../../../utils/floor-number'
import { formatNumber } from '../../../../../../../../../../../../utils/formatters'

import { goToChat } from '../../../../../../../../../../../../utils/go-to-chat'

import { useAppDispatch, useAppSelector } from '../../../../../../../../../../../../store/hooks'

import { fetchAllTransactions, selectedActiveLoan, updateLoan } from '../../../../../../../../../../../../store/reducers/loans'

import { ETransactionTypes } from '../../../../../../../../../../../../store/fetchers/users/types'

import { ICancelTransactionProps } from './cancel-transaction.props'
import styles from './styles.module.scss'

import { useRouter } from 'next/router'
import { allTransactions, setAllTransactions } from 'store/reducers/wallets'
import { selectedActiveDeposit, updateDeposit } from 'store/reducers/earnings'
import CopyId from 'components/ui/account/copy-id'
import { NetworkLabel } from 'components/ui/network-label'

export default function CancelTransaction({
  transaction,
  currency,
  setIsOpened,
  setOnAdditionPopupClose,
  onCancelSuccess,
}: ICancelTransactionProps) {
  const { t, replacePlaceholders } = useTranslation()

  const dispatch = useAppDispatch()

  const allWalletTransactions = useAppSelector(allTransactions)
  const activeLoan = useAppSelector(selectedActiveLoan)
  const activeDeposit = useAppSelector(selectedActiveDeposit)

  const router = useRouter()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isCanceled, setIsCanceled] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [title, setTitle] = useState<string>(t('cancel_transaction'))
  const [subtitle, setSubtitle] = useState<string>(t('click_cancel_transaction_button'))
  const initialTx = useRef(transaction)

  const confirmCancelTransaction = async () => {
    setIsLoading(true)
    const res = await cancelTransaction({
      transaction_id: transaction.transaction_id,
      transaction_type:
        transaction.transaction_type === ETransactionTypes.REPAYMENT
          ? ETransactionTypes.PLEDGE
          : transaction.transaction_type,
      account_type: transaction.account_type,
    })
    setIsLoading(false)
    if (res) {
      setIsCanceled(true)
      setIsError(false)

      if (router.asPath?.includes('account/dashboard') ||
        router.asPath?.includes('account/wallets') ||
        router.asPath?.includes('account/loans/info') ||
        router.asPath?.includes('account/savings/info')
      ) {
        if (router.asPath.includes('account/loans/info') &&
          transaction.transaction_id === activeLoan?.repayment?.transaction_id
        ) {
          dispatch(updateLoan({
            ...activeLoan,
            repayment: {
              ...activeLoan?.repayment,
              receive_address: "",
              receive_extra_id: "",
              transaction_status: null,
              send_address: null,
              send_extra_id: null,
              transaction_hash: null,
              active: false,
              transaction_id: null,
              payout_tx: {
                hash: null,
                amount: null,
                timestamp: null,
                id: null,
                status: null,
                incoming: false
              },
              payin_txs: []
            }
          }))
        }

        if (router.asPath.includes('account/savings/info') &&
          transaction.transaction_id === activeDeposit?.increase?.transaction_id) {
          dispatch(updateDeposit({
            ...activeDeposit,
            increase: {
              ...activeDeposit.increase,
              active: false,
              amount: null,
              transaction_status: null,
              send_address: null,
              send_extra_id: null,
              transaction_hash: null,
              transaction_id: "",
              expected_amount: null,
              payin_txs: [{
                ...activeDeposit.increase.payin_txs[0],
                status: 'finished',
              }]
            }
          }))
        }

        dispatch(setAllTransactions(allWalletTransactions?.filter(
          it => it.transaction_id !== transaction.transaction_id)))

        if (onCancelSuccess) onCancelSuccess()

        return
      }

      if (onCancelSuccess) onCancelSuccess()

      dispatch(fetchAllTransactions())
    } else {
      setIsError(true)
      setIsCanceled(false)
    }
  }

  useEffect(() => {
    if (isCanceled) {
      setTitle(t('transaction_cancelled'))
      setSubtitle(null)
    } else if (isError) {
      setTitle(t('failed_cancel_transaction'))
      setSubtitle(t('working_on_problem'))
    } else {
      setTitle(t('cancel_transaction'))
      setSubtitle(t('click_cancel_transaction_button'))
    }
  }, [isCanceled, isError])
  return (
    <Popup
      heading={title}
      subtitle={subtitle}
      containerClassname={cn(styles.popup, { [styles.canceled]: isCanceled }, { [styles.error]: isError })}
      headerClassname={styles.popup__title}
      subtitleClassname={cn(styles.popup__subtitle, { [styles.error]: isError })}
      onClose={() => {
        setIsOpened(false)
        if (setOnAdditionPopupClose) {
          setOnAdditionPopupClose(false)
        }
      }}
    >
      {!isCanceled && !isError && (
        <>
          <div className={styles.main}>
            <CopyId id={transaction.transaction_id} className={styles.id} />

            <div className={styles.data}>
              <div className={styles.data__item}>
                <h5>{t('amount')}</h5>
                <div className={styles.coin}>
                  {currency && (
                    <img
                      alt={currency?.code}
                      src={currency?.logo_url}
                    />
                  )}
                  <div className={styles.coin__data}>
                    <span className={styles.code}>
                      {formatNumber(floorBigNumber(transaction?.from_amount, 6))} {currency?.code?.toUpperCase()}
                    </span>
                    {currency && <NetworkLabel className={styles.network} currency={currency} />}
                  </div>
                </div>
              </div>

              <div className={styles.data__item}>
                <h5>{replacePlaceholders(t('to_this_address'), { currency: '' })}</h5>
                <div className={styles.address}>
                  <p>{transaction.from_address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.buttons}>
            <ButtonBlue isLoading={isLoading} className={styles.cancel} onClick={confirmCancelTransaction}>
              {t('cancel_transaction')}
            </ButtonBlue>
          </div>
        </>
      )}

      {isCanceled && (
        <>
          <div className={styles.main}>
            <CopyId id={initialTx?.current?.transaction_id} className={styles.id} />

            <img src="/assets/common-icons/Success.svg" className={styles.img} />

            <div className={styles.data}>
              <div className={styles.data__item}>
                <h5>{t('amount')}</h5>
                <div className={styles.coin}>
                  {currency && (
                    <img
                      alt={currency?.code}
                      src={currency?.logo_url}
                    />
                  )}
                  <div className={styles.coin__data}>
                    <span className={styles.code}>
                      {formatNumber(floorBigNumber(initialTx?.current?.from_amount, 6))} {currency?.code?.toUpperCase()}
                    </span>
                    {currency && <NetworkLabel className={styles.network} currency={currency} />}
                  </div>
                </div>
              </div>

              <div className={styles.data__item}>
                <h5>{replacePlaceholders(t('to_this_address'), { currency: '' })}</h5>
                <div className={styles.address}>
                  <p>{initialTx?.current?.from_address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.buttons}>
            <ButtonBlue
              className={cn(styles.cancel, styles.canceled)}
              onClick={() => {
                setIsOpened(false)
                if (setOnAdditionPopupClose) {
                  setOnAdditionPopupClose(false)
                }
              }}
            >
              {t('close')}
            </ButtonBlue>
          </div>
        </>
      )}

      {isError && (
        <>
          <div className={styles.main}>
            <CopyId id={transaction.transaction_id} className={styles.id} />

            <img src="/assets/common-icons/Warn.svg" className={cn(styles.img, styles.error)} />

            <div className={styles.data}>
              <div className={styles.data__item}>
                <h5>{t('amount')}</h5>
                <div className={styles.coin}>
                  {currency && (
                    <img
                      alt={currency?.code}
                      src={currency?.logo_url}
                    />
                  )}
                  <div className={styles.coin__data}>
                    <span className={styles.code}>
                      {formatNumber(floorBigNumber(transaction?.from_amount, 6))} {currency?.code?.toUpperCase()}
                    </span>
                    {currency && <NetworkLabel className={styles.network} currency={currency} />}
                  </div>
                </div>
              </div>

              <div className={styles.data__item}>
                <h5>{replacePlaceholders(t('to_this_address'), { currency: '' })}</h5>
                <div className={styles.address}>
                  <p>{transaction.from_address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.buttons}>
            <ButtonWhite
              className={styles.cancel}
              onClick={() => {
                setIsOpened(false)
                if (setOnAdditionPopupClose) {
                  setOnAdditionPopupClose(false)
                }
              }}
            >
              {t('try_later')}
            </ButtonWhite>
            <ButtonBlue className={styles.cancel} onClick={goToChat}>
              {t('contact_support')}
            </ButtonBlue>
          </div>
        </>
      )}
    </Popup>
  )
}
